/* eslint-disable @typescript-eslint/naming-convention -- JSON-LD file has it's own format. */
import type { Listing } from '@thevillages/schemas';

import type { ListingNoOpenHouseJsonLd } from '../types/listing-no-open-house-json-ld';

export function ListingToJsonLd(listing: Listing, href: string): ListingNoOpenHouseJsonLd {
  const _isMiddleton = listing.communities.some(
    (c: { id: string; title: string }) => c.id === 'middleton',
  );

  const _output: ListingNoOpenHouseJsonLd = {
    '@context': 'https://schema.org',
    '@id': href,
    '@type': 'RealEstateListing',
    datePosted: new Date(listing.date).toISOString().substring(0, 10),
    description: listing.description ?? 'Home for sale',
    identifier: {
      '@type': 'PropertyValue',
      name: listing.saleType === 'new' ? 'VNH' : 'VLS',
      value: listing.vls,
    },
    image: listing.images?.[0] ?? '',
    isFamilyFriendly: _isMiddleton,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This condition is necessary until we update the logic to fit the new types package.
    name: `${listing.series?.title}: ${listing.model?.title} at ${listing.address.streetAddress}`,
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      itemCondition:
        listing.saleType === 'new'
          ? 'https://schema.org/NewCondition'
          : 'https://schema.org/UsedCondition',
      itemOffered: {
        '@type': 'SingleFamilyResidence',
        additionalProperty: [
          {
            '@type': 'PropertyValue',
            name: 'Series',
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This condition is necessary until we update the logic to fit the new types package.
            value: listing.series?.title || '',
          },
        ],
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'US',
          addressLocality: listing.address.locality,
          addressRegion: listing.address.region,
          postalCode: listing.address.postalCode,
          streetAddress: listing.address.streetAddress,
        },
        floorSize: {
          '@type': 'QuantitativeValue',
          unitCode: 'FTK',
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This condition is necessary until we update the logic to fit the new types package.
          value: listing.squareFeet?.total || 0,
        },
        name: 'Properties of The Villages',
        numberOfBathroomsTotal: listing.baths?.full ?? 0,
        numberOfBedrooms: listing.beds ?? 0,
        numberOfRooms: listing.beds ?? 0,
      },
      price: listing.price,
      priceCurrency: 'USD',
      seller: {
        '@type': 'RealEstateAgent',
        areaServed: _isMiddleton ? 'Middleton and The Villages' : 'The Villages',
        name: 'Properties of The Villages',
        telephone: '352-753-6655',
        url: 'https://www.thevillages.com/sales-reps/',
      },
      url: href,
    },
    url: href,
  };

  if (listing.listingReps.length === 1) {
    _output.offers.seller.employee = {
      '@type': 'Person',
      email: listing.listingReps[0].email,
      jobTitle: 'Sales Associate',
      name: `${listing.listingReps[0].firstName} ${listing.listingReps[0].lastName}`,
      telephone: listing.listingReps[0].phone ?? '',
      url: `https://www.thevillages.com/sales-reps/${listing.listingReps[0].firstName.toLocaleLowerCase()}-${listing.listingReps[0].lastName.toLocaleLowerCase()}`,
    };
  } else if (listing.listingReps.length) {
    _output.offers.seller.employees = [];

    for (const rep of listing.listingReps) {
      _output.offers.seller.employees.push({
        '@type': 'Person',
        email: rep.email,
        jobTitle: 'Sales Associate',
        name: `${rep.firstName} ${rep.lastName}`,
        telephone: rep.phone ?? '',
        url: `https://www.thevillages.com/sales-reps/${rep.firstName.toLocaleLowerCase()}-${rep.lastName.toLocaleLowerCase()}`,
      });
    }
  }

  if (listing.virtualTour) {
    _output.offers.itemOffered.tourBookingPage = listing.virtualTour;
  }

  return _output;
}
/* eslint-enable @typescript-eslint/naming-convention -- Re-enable the rule */
